// Phase 0094-P1 — Salary Structures admin.
import { useState } from 'react'
import {
  App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row,
  Select, Skeleton, Space, Table, Tag, Typography,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { payrollApi, type SalaryStructure } from '@/features/payroll/payrollApi'
import apiClient from '@/shared/api/client'
import ShortageRatesCard from './ShortageRatesCard'

const { Text, Title } = Typography

interface Emp { id: string; emp_code: string; emp_name: string }

export default function SalaryStructuresPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [employeeId, setEmployeeId] = useState<string | undefined>()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: employees } = useQuery({
    queryKey: ['employees-for-payroll'],
    queryFn: async () => (await apiClient.get<Emp[]>('/employees')).data,
  })
  const { data: heads } = useQuery({
    queryKey: ['payroll-heads'],
    queryFn: () => payrollApi.heads({ active_only: true }),
  })
  const { data: structures, isLoading } = useQuery({
    queryKey: ['salary-structures', employeeId],
    queryFn: () => payrollApi.listStructures(employeeId),
    enabled: !!employeeId,
  })

  const createMut = useMutation({
    mutationFn: (vals: Record<string, unknown>) =>
      payrollApi.createStructure(vals),
    onSuccess: (r: any) => {
      // GOV-001 P5 — structure changes apply only after CFO approval.
      if (r?.approval_status === 'PENDING_APPROVAL') {
        message.info(r.message ?? 'Salary structure sent for CFO approval')
      } else {
        message.success('Salary structure created.')
      }
      qc.invalidateQueries({ queryKey: ['salary-structures', employeeId] })
      setOpen(false)
      form.resetFields()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const approveMut = useMutation({
    mutationFn: (id: string) => payrollApi.approveStructure(id),
    onSuccess: () => {
      message.success('Approved.')
      qc.invalidateQueries({ queryKey: ['salary-structures', employeeId] })
    },
  })

  const columns = [
    { title: 'Effective From', dataIndex: 'effective_from', key: 'effective_from' },
    { title: 'Effective To', dataIndex: 'effective_to', key: 'effective_to',
      render: (s: string | null) => s ?? '—' },
    { title: 'Salary Type', dataIndex: 'salary_type', key: 'salary_type',
      render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Basic', dataIndex: 'basic_amount', key: 'basic_amount',
      render: (n: number | null) => n ? `₹${n.toLocaleString()}` : '—' },
    { title: 'Per Day', dataIndex: 'per_day_rate', key: 'per_day_rate',
      render: (n: number | null) => n ? `₹${n}` : '—' },
    { title: 'Per Hour', dataIndex: 'per_hour_rate', key: 'per_hour_rate',
      render: (n: number | null) => n ? `₹${n}` : '—' },
    { title: 'OT Rate', dataIndex: 'ot_rate', key: 'ot_rate',
      render: (n: number | null) => n ? `₹${n}` : '—' },
    { title: 'Status', dataIndex: 'approval_status', key: 'approval_status',
      render: (s: string) => <Tag color={s === 'APPROVED' ? 'success' : 'warning'}>{s}</Tag> },
    { title: '', key: 'actions',
      render: (_: unknown, r: SalaryStructure) => (
        r.approval_status !== 'APPROVED'
          ? <Button size="small" onClick={() => approveMut.mutate(r.id)}>Approve</Button>
          : <Text type="success">✓</Text>
      ) },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Salary Structures</Title>
      <Text type="secondary">
        Versioned per-employee structure with earning + deduction lines.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Space wrap>
          <Select
            showSearch
            placeholder="Pick an employee"
            style={{ minWidth: 320 }}
            value={employeeId}
            onChange={setEmployeeId}
            optionFilterProp="label"
            options={(employees ?? []).map((e) => ({
              value: e.id, label: `${e.emp_code} — ${e.emp_name}`,
            }))}
          />
          <Button icon={<PlusOutlined />} type="primary"
                  disabled={!employeeId}
                  onClick={() => {
                    form.resetFields()
                    form.setFieldsValue({
                      employee_id: employeeId,
                      salary_type: 'MONTHLY',
                      effective_from: dayjs(),
                    })
                    setOpen(true)
                  }}>
            New structure
          </Button>
        </Space>
      </Card>

      {employeeId && (
        <Card style={{ marginTop: 16 }} title="History">
          {isLoading ? <Skeleton active /> : (
            <Table
              rowKey="id"
              columns={columns as never}
              dataSource={structures ?? []}
              pagination={false}
              size="small"
            />
          )}
        </Card>
      )}

      {/* HR-004 follow-up — Shortage rate rules (plant-wide, not per
          employee). Lives here because rates ARE part of payroll config. */}
      <ShortageRatesCard />

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="New salary structure"
        width={720}
        onOk={() => {
          form.validateFields().then((vals) => {
            createMut.mutate({
              ...vals,
              employee_id: employeeId,
              effective_from: vals.effective_from.format('YYYY-MM-DD'),
              lines: (vals.lines ?? []).filter((l: { head_code?: string }) => l?.head_code),
            })
          })
        }}
        confirmLoading={createMut.isPending}
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Salary type" name="salary_type" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'MONTHLY' }, { value: 'DAILY_WAGE' },
                  { value: 'HOURLY' }, { value: 'CONTRACT' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Effective from" name="effective_from" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Basic" name="basic_amount">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Per Day Rate" name="per_day_rate">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="OT Rate" name="ot_rate">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                <Text>Earning / Deduction lines</Text>
                {fields.map((f) => (
                  <Row key={f.key} gutter={8} style={{ marginTop: 6 }}>
                    <Col span={10}>
                      <Form.Item name={[f.name, 'head_code']} noStyle>
                        <Select placeholder="Head" style={{ width: '100%' }}
                          options={(heads ?? []).map((h) => ({
                            value: h.head_code,
                            label: `${h.head_code} (${h.head_type})`,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name={[f.name, 'amount']} noStyle>
                        <InputNumber placeholder="Amount" min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name={[f.name, 'percentage']} noStyle>
                        <InputNumber placeholder="%" min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button size="small" danger onClick={() => remove(f.name)}>×</Button>
                    </Col>
                  </Row>
                ))}
                <Button block style={{ marginTop: 8 }}
                        onClick={() => add({ head_code: '', amount: null, percentage: null })}>
                  + Add line
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  )
}
